/**
 * useInspectorAlerts - aggregator that turns inspector-wide data sources
 * (sync freshness) into a uniform AlertItem[] consumed by
 * <InspectorAlertStack/>. Release notices are no longer an alert item - they
 * auto-open the release-notes dialog via <ReleaseNotesAutoLauncher/>.
 *
 * Does not subscribe to active sync operations itself - useActiveSyncStatus
 * is not internally deduped, so SyncStatusBarWrapper stays the single owner
 * of that subscription and pushes `isSyncing` down via prop.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import useSyncFreshness from './useSyncFreshness'
import useSyncTrigger from './useSyncTrigger'
import type { AlertItem } from '../components/inspector/AlertStack/types'

interface FranchiseDataLike {
  has_complete_lightspeed_connection?: boolean
  has_complete_xero_connection?: boolean
}

interface UseInspectorAlertsOptions {
  isSyncing: boolean
  onSyncStarted?: () => void
}

interface UseInspectorAlertsReturn {
  alerts: AlertItem[]
}

const freshnessDismissalKey = (userId: number | string, fpCode: string) =>
  `evop_freshness_banner_dismissed_${userId}_${fpCode.toLowerCase()}`

export const useInspectorAlerts = (
  fpCode: string,
  franchiseData: FranchiseDataLike | null | undefined,
  { isSyncing, onSyncStarted }: UseInspectorAlertsOptions,
): UseInspectorAlertsReturn => {
  const { user } = useAuth()
  const { worstTier, oldestAge } = useSyncFreshness(fpCode)
  const { triggerSync, loading: triggerLoading } = useSyncTrigger({
    fpCode,
    franchiseData,
    onSyncStarted,
  })

  const userKey = user?.id ?? 'anon'
  const storageKey = freshnessDismissalKey(userKey, fpCode)

  const [freshnessDismissed, setFreshnessDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(storageKey) === '1'
    } catch {
      return false
    }
  })

  useEffect(() => {
    try {
      setFreshnessDismissed(sessionStorage.getItem(storageKey) === '1')
    } catch {
      setFreshnessDismissed(false)
    }
  }, [storageKey])

  useEffect(() => {
    if (worstTier === 'fresh' && freshnessDismissed) {
      try {
        sessionStorage.removeItem(storageKey)
      } catch {}
      setFreshnessDismissed(false)
    }
  }, [worstTier, freshnessDismissed, storageKey])

  const dismissFreshness = useCallback(() => {
    try {
      sessionStorage.setItem(storageKey, '1')
    } catch {}
    setFreshnessDismissed(true)
  }, [storageKey])

  const alerts = useMemo<AlertItem[]>(() => {
    const out: AlertItem[] = []

    const freshnessVisible =
      worstTier !== 'fresh' && !isSyncing && !freshnessDismissed
    if (freshnessVisible) {
      const ageLabel = oldestAge ?? 'a while'
      out.push({
        id: 'sync-freshness',
        severity: 'warning',
        title: `Data is ${ageLabel} old`,
        message: 'Sync latest data before reviewing exceptions.',
        action: {
          label: 'Sync latest data',
          onClick: () => {
            void triggerSync()
          },
          disabled: triggerLoading,
        },
        dismissible: true,
        onDismiss: dismissFreshness,
      })
    }

    return out
  }, [
    worstTier,
    isSyncing,
    freshnessDismissed,
    oldestAge,
    triggerSync,
    triggerLoading,
    dismissFreshness,
  ])

  return { alerts }
}

export default useInspectorAlerts
